import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft, Plus, Trash2, Edit3, Book, FileText, AlertCircle, CheckCircle, Globe, PenTool, BookOpen } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { calculateTotalWords, validateBukuOutline, BOOK_TYPES, WRITING_STYLES, LANGUAGES } from './bukuUtils'

const BukuOutlinePage = ({ query, outline, onOutlineChange, onStartGeneration, onBack, llmService }) => {
  const [editingSection, setEditingSection] = useState(null)
  const [newSectionTitle, setNewSectionTitle] = useState('')
  const [showAddSection, setShowAddSection] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES.INDONESIAN)
  const [selectedBookType, setSelectedBookType] = useState(BOOK_TYPES.PRACTICAL)
  const [selectedWritingStyle, setSelectedWritingStyle] = useState(WRITING_STYLES.CONVERSATIONAL)

  const handleEditSection = (sectionId, field, value) => {
    const updatedOutline = outline.map(section => {
      if (section.id === sectionId) {
        return { ...section, [field]: value }
      }
      return section
    })
    onOutlineChange(updatedOutline)
  }

  const handleDeleteSection = (sectionId) => {
    const updatedOutline = outline.filter(section => section.id !== sectionId)
    onOutlineChange(updatedOutline)
  }

  const handleAddSection = () => {
    if (!newSectionTitle.trim()) return
    
    const newSection = {
      id: `custom_${Date.now()}`,
      title: newSectionTitle,
      minWords: 5000, // Default minimal words for book chapters
      description: '',
      isEditable: true,
      topic: query
    }
    
    onOutlineChange([...outline, newSection])
    setNewSectionTitle('')
    setShowAddSection(false)
  }

  const handleStartGeneration = () => {
    // Add book configuration to outline
    const configuredOutline = outline.map(section => ({
      ...section,
      language: selectedLanguage,
      bookType: selectedBookType,
      writingStyle: selectedWritingStyle
    }))
    onStartGeneration(configuredOutline)
  }

  const validation = validateBukuOutline(outline)
  const totalWords = calculateTotalWords(outline)

  // Book type options
  const bookTypeOptions = [
    { value: BOOK_TYPES.PRACTICAL, label: 'Praktis', description: 'Buku panduan praktis dengan tips dan strategi' },
    { value: BOOK_TYPES.ACADEMIC, label: 'Akademik', description: 'Buku ilmiah dengan pendekatan akademis' },
    { value: BOOK_TYPES.TUTORIAL, label: 'Tutorial', description: 'Buku pembelajaran step-by-step' },
    { value: BOOK_TYPES.BUSINESS, label: 'Bisnis', description: 'Buku fokus pada strategi dan praktik bisnis' },
    { value: BOOK_TYPES.SELF_HELP, label: 'Self-Help', description: 'Buku pengembangan diri dan motivasi' },
    { value: BOOK_TYPES.TECHNICAL, label: 'Teknis', description: 'Buku teknis dengan detail implementasi' },
    { value: BOOK_TYPES.EDUCATIONAL, label: 'Edukasi', description: 'Buku pembelajaran dan pendidikan' },
    { value: BOOK_TYPES.REFERENCE, label: 'Referensi', description: 'Buku rujukan dan ensiklopedia' }
  ]

  // Writing style options
  const writingStyleOptions = [
    { value: WRITING_STYLES.CONVERSATIONAL, label: 'Percakapan', description: 'Gaya santai dan mudah dipahami' },
    { value: WRITING_STYLES.FORMAL, label: 'Formal', description: 'Gaya resmi dan profesional' },
    { value: WRITING_STYLES.ACADEMIC, label: 'Akademik', description: 'Gaya ilmiah dengan sitasi lengkap' },
    { value: WRITING_STYLES.PRACTICAL, label: 'Praktis', description: 'Fokus pada implementasi dan action' },
    { value: WRITING_STYLES.STORYTELLING, label: 'Bercerita', description: 'Menggunakan narasi dan cerita' },
    { value: WRITING_STYLES.INSTRUCTIONAL, label: 'Instruksional', description: 'Panduan langkah demi langkah' }
  ]

  // Language options
  const languageOptions = [
    { value: LANGUAGES.INDONESIAN, label: 'Bahasa Indonesia', flag: '🇮🇩' },
    { value: LANGUAGES.ENGLISH, label: 'English', flag: '🇺🇸' }
  ]

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Header */}
      <div className="bg-bg-secondary border-b border-border-primary sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-3 py-2 text-text-tertiary hover:text-text-primary transition-colors rounded-lg hover:bg-bg-tertiary"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Kembali</span>
              </button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Book className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-text-primary">Buku Agent</h1>
                  <p className="text-sm text-text-tertiary">Konfigurasi Outline Buku</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleStartGeneration}
              disabled={!validation.isValid}
              className={cn(
                buttonVariants.primary, 
                "flex items-center",
                !validation.isValid && "opacity-50 cursor-not-allowed"
              )}
            >
              <FileText className="w-4 h-4 mr-2" />
              Buat Buku
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Topic Display */}
        <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
          <h2 className="text-lg font-semibold text-text-primary mb-2">Topik Buku</h2>
          <p className="text-text-secondary text-lg">"{query}"</p>
        </div>

        {/* Book Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Language Selection */}
          <div className="bg-bg-secondary rounded-xl p-6 border border-border-primary">
            <div className="flex items-center space-x-2 mb-4">
              <Globe className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-semibold text-text-primary">Bahasa</h3>
            </div>
            <div className="space-y-2">
              {languageOptions.map((option) => (
                <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="language"
                    value={option.value}
                    checked={selectedLanguage === option.value}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-lg">{option.flag}</span>
                  <span className="text-text-secondary">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Book Type Selection */}
          <div className="bg-bg-secondary rounded-xl p-6 border border-border-primary">
            <div className="flex items-center space-x-2 mb-4">
              <BookOpen className="w-5 h-5 text-green-500" />
              <h3 className="text-lg font-semibold text-text-primary">Jenis Buku</h3>
            </div>
            <select
              value={selectedBookType}
              onChange={(e) => setSelectedBookType(e.target.value)}
              className="w-full bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {bookTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-text-tertiary mt-2">
              {bookTypeOptions.find(opt => opt.value === selectedBookType)?.description}
            </p>
          </div>

          {/* Writing Style Selection */}
          <div className="bg-bg-secondary rounded-xl p-6 border border-border-primary">
            <div className="flex items-center space-x-2 mb-4">
              <PenTool className="w-5 h-5 text-purple-500" />
              <h3 className="text-lg font-semibold text-text-primary">Gaya Penulisan</h3>
            </div>
            <select
              value={selectedWritingStyle}
              onChange={(e) => setSelectedWritingStyle(e.target.value)}
              className="w-full bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {writingStyleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-text-tertiary mt-2">
              {writingStyleOptions.find(opt => opt.value === selectedWritingStyle)?.description}
            </p>
          </div>
        </div>

        {/* Validation Status */}
        {!validation.isValid && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-400 mb-2">Outline belum memenuhi persyaratan:</h3>
                <ul className="text-sm text-red-300 space-y-1">
                  {validation.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Outline Sections */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-text-primary">Outline Buku</h2>
            <button
              onClick={() => setShowAddSection(true)}
              className={cn(buttonVariants.secondary, "text-sm flex items-center")}
            >
              <Plus className="w-4 h-4 mr-2" />
              Tambah Bab
            </button>
          </div>

          {outline.map((section, index) => (
            <div key={section.id} className="bg-bg-secondary rounded-xl border border-border-primary overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-sm font-medium text-text-tertiary bg-bg-tertiary px-2 py-1 rounded">
                        #{index + 1}
                      </span>
                      {editingSection === section.id ? (
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => handleEditSection(section.id, 'title', e.target.value)}
                          onBlur={() => setEditingSection(null)}
                          onKeyDown={(e) => e.key === 'Enter' && setEditingSection(null)}
                          className="flex-1 bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-orange-500"
                          autoFocus
                        />
                      ) : (
                        <h3 className="text-lg font-semibold text-text-primary">{section.title}</h3>
                      )}
                    </div>
                    
                    {section.description && (
                      <p className="text-text-secondary mb-3">{section.description}</p>
                    )}
                    
                    {section.subsections && section.subsections.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-text-tertiary mb-2">Sub-bagian:</p>
                        <div className="flex flex-wrap gap-2">
                          {section.subsections.map((sub, subIndex) => (
                            <span key={subIndex} className="text-xs bg-bg-tertiary text-text-secondary px-2 py-1 rounded">
                              {typeof sub === 'string' ? sub : sub.title || sub.description || 'Sub-bagian'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {section.minWords > 0 && (
                      <div className="flex items-center space-x-4 text-sm text-text-tertiary">
                        <span>Target: {section.minWords.toLocaleString()} kata</span>
                        <span>≈ {Math.ceil(section.minWords / 250)} halaman</span>
                      </div>
                    )}
                  </div>
                  
                  {section.isEditable && (
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => setEditingSection(editingSection === section.id ? null : section.id)}
                        className="p-2 text-text-tertiary hover:text-text-primary transition-colors rounded-lg hover:bg-bg-tertiary"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSection(section.id)}
                        className="p-2 text-red-400 hover:text-red-300 transition-colors rounded-lg hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Add New Section Form */}
          {showAddSection && (
            <div className="bg-bg-secondary rounded-xl border border-border-primary p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Tambah Bab Baru</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-tertiary mb-2">Judul Bab</label>
                  <input
                    type="text"
                    value={newSectionTitle}
                    onChange={(e) => setNewSectionTitle(e.target.value)}
                    placeholder="Masukkan judul bab..."
                    className="w-full bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleAddSection}
                    disabled={!newSectionTitle.trim()}
                    className={cn(
                      buttonVariants.primary,
                      "text-sm flex items-center",
                      !newSectionTitle.trim() && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Tambah
                  </button>
                  <button
                    onClick={() => setShowAddSection(false)}
                    className={cn(buttonVariants.secondary, "text-sm flex items-center")}
                  >
                    Batal
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="mt-8 bg-bg-secondary rounded-xl p-6 border border-border-primary">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">Ringkasan Buku</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-text-tertiary">Total Bab:</span>
                  <p className="font-medium text-text-primary">{validation.chapterCount}</p>
                </div>
                <div>
                  <span className="text-text-tertiary">Target Kata:</span>
                  <p className="font-medium text-text-primary">{totalWords.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-text-tertiary">Estimasi Halaman:</span>
                  <p className="font-medium text-text-primary">{Math.ceil(totalWords / 250)}</p>
                </div>
                <div>
                  <span className="text-text-tertiary">Status:</span>
                  <p className={cn(
                    "font-medium flex items-center space-x-1",
                    validation.isValid ? "text-green-400" : "text-red-400"
                  )}>
                    {validation.isValid ? (
                      <><CheckCircle className="w-4 h-4" /><span>Siap</span></>
                    ) : (
                      <><AlertCircle className="w-4 h-4" /><span>Belum Siap</span></>
                    )}
                  </p>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleStartGeneration}
              disabled={!validation.isValid}
              className={cn(
                buttonVariants.primary,
                "text-lg px-8 py-3 flex items-center",
                !validation.isValid && "opacity-50 cursor-not-allowed"
              )}
            >
              <FileText className="w-5 h-5 mr-3" />
              Buat Buku
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

BukuOutlinePage.propTypes = {
  query: PropTypes.string.isRequired,
  outline: PropTypes.array.isRequired,
  onOutlineChange: PropTypes.func.isRequired,
  onStartGeneration: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired
}

export default BukuOutlinePage